export type ScopeInfo = {
  name: string;
  description: string;
};

export type ScopeCategory = {
  category: string;
  categoryLabel: string;
  scopes: ScopeInfo[];
};

type ScopeAccessGroup = {
  accessType: 'read' | 'write';
  categories: ScopeCategory[];
};

export type ScopeGroup = {
  product: string;
  productLabel: string;
  readScopes: ScopeInfo[];
  writeScopes: ScopeInfo[];
  accessGroups: ScopeAccessGroup[];
};

export function isReadScope(scopeName: string): boolean {
  return scopeName.endsWith(':list');
}

function getProductKey(scopeName: string): string {
  if (scopeName.startsWith('harvest:')) {
    return 'recruiting';
  }
  if (scopeName.startsWith('auditlog:')) {
    return 'audit_log';
  }
  if (scopeName.includes('.')) {
    return 'candidate_ingestion';
  }
  return 'other';
}

// Extracts the resource/category from a scope name.
// e.g. "harvest:candidates:list" → "candidates"
//      "harvest:job_interview_stages:list" → "job_interview_stages"
function getCategoryKey(scopeName: string): string {
  const parts = scopeName.split(':');
  if (parts.length >= 3) {
    return parts[1];
  }
  // For dot-separated scopes like "candidates.view"
  const dotParts = scopeName.split('.');
  if (dotParts.length >= 2) {
    return dotParts[0];
  }
  return 'other';
}

function titleize(str: string): string {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function groupByCategory(scopes: ScopeInfo[]): ScopeCategory[] {
  const categoryMap = new Map<string, ScopeInfo[]>();

  for (const scope of scopes) {
    const category = getCategoryKey(scope.name);
    const existing = categoryMap.get(category);
    if (existing) {
      existing.push(scope);
    } else {
      categoryMap.set(category, [scope]);
    }
  }

  return Array.from(categoryMap.entries()).map(
    ([category, categoryScopes]) => ({
      category,
      categoryLabel: titleize(category),
      scopes: categoryScopes,
    })
  );
}

function buildAccessGroups(
  readScopes: ScopeInfo[],
  writeScopes: ScopeInfo[]
): ScopeAccessGroup[] {
  const groups: ScopeAccessGroup[] = [];

  if (readScopes.length > 0) {
    groups.push({
      accessType: 'read',
      categories: groupByCategory(readScopes),
    });
  }

  if (writeScopes.length > 0) {
    groups.push({
      accessType: 'write',
      categories: groupByCategory(writeScopes),
    });
  }

  return groups;
}

export function groupScopes(
  scopeList: ScopeInfo[],
  translationFn: (key: string) => string
): ScopeGroup[] {
  const groupMap = new Map<
    string,
    { readScopes: ScopeInfo[]; writeScopes: ScopeInfo[] }
  >();

  for (const scope of scopeList) {
    const product = getProductKey(scope.name);
    if (!groupMap.has(product)) {
      groupMap.set(product, { readScopes: [], writeScopes: [] });
    }
    const entry = groupMap.get(product);
    if (entry === undefined) {
      continue;
    }
    if (isReadScope(scope.name)) {
      entry.readScopes.push(scope);
    } else {
      entry.writeScopes.push(scope);
    }
  }

  return Array.from(groupMap.entries()).map(
    ([product, { readScopes, writeScopes }]) => ({
      product,
      productLabel: translationFn(`product_groups.${product}`),
      readScopes,
      writeScopes,
      accessGroups: buildAccessGroups(readScopes, writeScopes),
    })
  );
}
